import List from '@pelckmans/elementis/components/list';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ModuleContext from '../../_contexts/ModuleContext';
import Pill from '@pelckmans/elementis/components/pill';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';
import useLicenses from '../../../../hooks/useLicenses';
import stripHtml from '../../../../utils/wordlist/stripHtml';
import { SearchResultTextAnnotation } from './../api/search-api';
import AccountContext from '../../../../contexts/AccountContext';
import { buildCodexUrl } from '../../utils/digibookAppUtils';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import useAnalytics from '../../../../hooks/useAnalytics';

interface Props {
  searchItem: SearchResultTextAnnotation;
}

function SearchResultTextAnnotationItem({ searchItem }: Props) {
  const { module, allModules } = useContext(ModuleContext);
  const { t } = useTranslation();
  const { isModuleLicensed } = useLicenses();
  const { account } = useContext(AccountContext);
  const analytics = useAnalytics();

  const targetModule = allModules.find(m => m.id === searchItem.moduleId);

  if (!module || !targetModule) return null;
  const licensedModule = isModuleLicensed(targetModule);

  const name = searchItem.metadata.digibookName;
  const content = searchItem.name ? searchItem.name : undefined;

  return (
    <List.Item
      key={searchItem.id}
      as="button"
      title={!licensedModule ? stripHtml(name) : name}
      subText={content}
      disabled={!licensedModule}
      onClick={() => {
        analytics.capture({
          eventType: ANALYTICS_EVENT_TYPES.DIGIBOOK_ACCESSED,
          objectId: searchItem.metadata.digibookId,
          subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SEARCH,
        });

        const url = buildCodexUrl({
          digibookId: searchItem.metadata.digibookId,
          superModuleId: module.id,
          accountProvider: account?.provider,
          page: searchItem.pageNumber,
          annotationSetId: searchItem.metadata.annotationSetId,
        });
        window.open(url, '_blank', 'noopener,noreferrer');
      }}
      pillGroupProps={buildGroupProps(
        <>
          <Pill key="scope-pill">{t('PAGES.SEARCH.RESULTS.TEXT_ANNOTATION')}</Pill>
          <Pill
            key="module-pill"
            size="sm"
            avatar={
              <Avatar>
                <AvatarImage size="xs" name={targetModule!.niceName} src={targetModule!.styling?.logo || ''} />
              </Avatar>
            }
          >
            {targetModule?.niceName}
          </Pill>
          {searchItem.metadata.annotationSetName && (
            <Pill key="annotation-set-pill">{searchItem.metadata.annotationSetName}</Pill>
          )}
          {searchItem.hierarchy && searchItem.hierarchy.length > 0 && (
            <Pill key="hierarchy-pill" icon={<ChapterIcon />}>
              {getFullHierarchyDescriptionOfNode(searchItem.hierarchy, t)}
            </Pill>
          )}
        </>,
      )}
    />
  );
}

export default SearchResultTextAnnotationItem;
